import { useState, useRef, useEffect, type Dispatch, type SetStateAction, type MutableRefObject } from "react";
import { api, type RfqItemQuote, type ServiceQuote, type ServiceQuoteInput, type ArrangementRequest, type ArrangementQuote, type ArrangementQuoteInput, type PurchaseOfferReview } from "../../lib/api";
import type { PurchasePreview } from "../../components/PurchaseConfirmation";
import { useAuth } from "../../lib/auth";
import { preparePurchaseReview, purchaseCheckoutInput } from "../../lib/purchaseReview";
import { ApiError } from "../../lib/api-client/transport";
type Conversation = Awaited<ReturnType<typeof api.getThread>>;
export type QuoteTarget = { serviceId?: string | null; businessItemId?: string | null; rfqId?: string; serviceTitle: string; requestMessageId?: string; initialQuote?: ServiceQuote };

type ConversationActionsInput = {
  user: ReturnType<typeof useAuth>["user"]; activeId: string | null; convo: Conversation | null;
  conversationRequestRef: MutableRefObject<number>; conversationReadRef: MutableRefObject<number>;
  setConvo: Dispatch<SetStateAction<Conversation | null>>; setErr: Dispatch<SetStateAction<string | null>>;
  refreshConversation: (id: string, request: number) => Promise<void>; navigate: (path: string) => void;
};
export function useConversationActions(input: ConversationActionsInput) {
  const { user, activeId, convo, conversationRequestRef, conversationReadRef, setConvo, setErr, refreshConversation, navigate } = input;
  const [quoteTarget, setQuoteTarget] = useState<QuoteTarget | null>(null);
  const [arrangementTarget, setArrangementTarget] = useState<{ request: ArrangementRequest; quote?: ArrangementQuote; purpose?: "counter" } | null>(null);
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [acceptanceToast, setAcceptanceToast] = useState<string | null>(null);
  const [purchaseTarget, setPurchaseTarget] = useState<{ preview: PurchasePreview; threadId: string; request: number } | null>(null);
  const [purchaseBusy, setPurchaseBusy] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const purchasePending = useRef(false);
  useEffect(() => {
    if (!acceptanceToast) return;
    const timer = setTimeout(() => setAcceptanceToast(null), 7000);
    return () => clearTimeout(timer);
  }, [acceptanceToast]);
  useEffect(() => { setAcceptanceToast(null); }, [user?.id]);
  useEffect(() => { setPurchaseTarget(null); setPurchaseError(null); setPurchaseBusy(false); }, [activeId, user?.id]);
  function openServiceQuote(target: QuoteTarget) { if (!quoteBusy) { setArrangementTarget(null); setQuoteTarget(target); } }

  async function previewQuote(quote: ServiceQuoteInput) {
    if (!activeId || !quoteTarget) throw new Error("Quotation source is unavailable.");
    const request = conversationRequestRef.current;
    const result = quoteTarget.rfqId ? await api.previewRfqServiceQuote(quoteTarget.rfqId, quote)
      : quoteTarget.businessItemId && quoteTarget.requestMessageId ? await api.previewBusinessQuote({ threadId: activeId, businessItemId: quoteTarget.businessItemId, requestMessageId: quoteTarget.requestMessageId }, quote)
      : quoteTarget.serviceId && quoteTarget.requestMessageId ? await api.previewServiceQuote(activeId, quoteTarget.serviceId, quoteTarget.requestMessageId, quote)
      : null;
    if (request !== conversationRequestRef.current) throw new Error("Conversation changed. Reopen the quotation.");
    if (!result) throw new Error("Quotation source is unavailable.");
    return result.document;
  }

  async function sendQuote(quote: ServiceQuoteInput) {
    if (!activeId || !quoteTarget || quoteBusy) return;
    const request = conversationRequestRef.current;
    setQuoteBusy(true); setErr(null);
    try {
      if (quoteTarget.rfqId) await api.sendRfqServiceQuote(quoteTarget.rfqId, quote);
      else if ((quoteTarget.serviceId || quoteTarget.businessItemId) && quoteTarget.requestMessageId) await api.sendServiceQuote(activeId, quoteTarget.serviceId ?? null, quoteTarget.requestMessageId, quote, quoteTarget.businessItemId);
      else throw new Error("Quotation source is unavailable.");
      if (request !== conversationRequestRef.current) return;
      setQuoteTarget(null);
      try { await refreshConversation(activeId, request); }
      catch { if (request === conversationRequestRef.current) setErr("Quotation sent, but the conversation could not refresh. Reopen it to load the saved quotation."); }
    } catch (error: unknown) { if (request === conversationRequestRef.current) throw error; }
    finally { if (request === conversationRequestRef.current) setQuoteBusy(false); }
  }

  async function updateQuote(quote: ServiceQuote, action: "accept" | "changes", note = "") {
    if (!activeId || quoteBusy) return;
    const request = conversationRequestRef.current;
    setQuoteBusy(true); setErr(null);
    try {
      if (action === "accept") await api.acceptServiceQuote(activeId, quote.id, quote.document?.id);
      else await api.requestServiceQuoteChanges(activeId, quote.id, note);
      if (request !== conversationRequestRef.current) return;
      try { await refreshConversation(activeId, request); }
      catch { if (request === conversationRequestRef.current) setErr(action === "accept"
        ? "Quotation accepted, but the conversation could not refresh. Reopen it to load the contract."
        : "Changes requested, but the conversation could not refresh. Reopen it to load the saved request."); }
    } catch (error: unknown) { if (request === conversationRequestRef.current) setErr(error instanceof Error ? error.message : "Could not update quotation."); }
    finally { if (request === conversationRequestRef.current) setQuoteBusy(false); }
  }

  async function acceptArrangementRequest(arrangement: ArrangementRequest, review?: PurchaseOfferReview) {
    // [items.request-acceptance](../../../../docs/product/items.md#items-request-acceptance): seller consent uses the saved buyer snapshot. A committed acceptance
    // remains successful if the subsequent read fails; stale chat responses stay scoped.
    if (!activeId || quoteBusy) return;
    const request = conversationRequestRef.current;
    setQuoteBusy(true); setErr(null); setAcceptanceToast(null);
    try {
      await api.acceptItemArrangementRequest(arrangement.id, review);
      if (request !== conversationRequestRef.current) return;
      setAcceptanceToast("Request accepted. No payment has been made; the buyer can now use Buy now to confirm and pay.");
      try { await refreshConversation(activeId, request); }
      catch { if (request === conversationRequestRef.current) setErr("Request accepted, but the conversation could not refresh. Reopen it to load the agreed terms."); }
    }
    catch (error: unknown) { if (request === conversationRequestRef.current) throw error; }
    finally { if (request === conversationRequestRef.current) setQuoteBusy(false); }
  }

  async function updateItemQuote(quote: RfqItemQuote, decision: "accept" | "changes" | "decline", itemIds: string[] = [], note = "", expectedDocumentId?: string) {
    if (!activeId || quoteBusy) return;
    const request = conversationRequestRef.current;
    setQuoteBusy(true); setErr(null);
    try {
      if (decision === "accept") {
        const accepted = await api.acceptRfqItemQuote(quote, itemIds, expectedDocumentId);
        if (request !== conversationRequestRef.current) return;
        navigate(`/contracts?contract=${encodeURIComponent(accepted.contractId)}`);
      } else { await api.reviewRfqItemQuote(quote, decision, note); await refreshConversation(activeId, request); }
    } catch (error: unknown) { if (request === conversationRequestRef.current) setErr(error instanceof Error ? error.message : "Could not update Items quotation."); }
    finally { if (request === conversationRequestRef.current) setQuoteBusy(false); }
  }

  async function sendArrangementQuote(terms: ArrangementQuoteInput) {
    if (!activeId || !arrangementTarget || quoteBusy) return;
    const request = conversationRequestRef.current;
    setQuoteBusy(true); setErr(null);
    try {
      await api.sendItemArrangementQuote(arrangementTarget.request.id, terms);
      if (request !== conversationRequestRef.current) return;
      setArrangementTarget(null);
      await refreshConversation(activeId, request);
    } catch (error: unknown) { if (request === conversationRequestRef.current) throw error; }
    finally { if (request === conversationRequestRef.current) setQuoteBusy(false); }
  }
  async function decideArrangement(quote: ArrangementQuote, decision: "accept" | "changes", note = "", proposal?: ArrangementQuoteInput, expectedDocumentId?: string) {
    if (!activeId || quoteBusy) return;
    const request = conversationRequestRef.current;
    setQuoteBusy(true); setErr(null); setAcceptanceToast(null);
    try {
      await api.decideItemArrangement(quote.id, decision, note, proposal, expectedDocumentId);
      if (request !== conversationRequestRef.current) return;
      // [items.buyer-decision](../../../../docs/product/items.md#items-buyer-decision): confirmed acceptance is local feedback, never a buyer-authored chat message.
      if (decision === "accept") setAcceptanceToast("Proposal accepted. No payment has been made; use Buy now to confirm and pay.");
      const status: ArrangementQuote["status"] = decision === "accept" ? "accepted" : "changes_requested";
      setConvo((current) => !current || current.thread.id !== activeId ? current : {
        ...current, messages: current.messages.map((message) => message.arrangementQuote?.id === quote.id
          ? { ...message, arrangementQuote: { ...message.arrangementQuote, status } } : message),
      });
      try { await refreshConversation(activeId, request); }
      catch { if (request === conversationRequestRef.current) setErr("Arrangement updated, but the conversation could not refresh. Reopen it to load the latest messages."); }
    }
    catch (error: unknown) { if (request === conversationRequestRef.current) throw error; }
    finally { if (request === conversationRequestRef.current) setQuoteBusy(false); }
  }

  function markArrangementPurchased(quoteId: string, orderId: string, threadId: string) {
    // [items.stock-and-replay](../../../../docs/product/items.md#items-stock-and-replay): committed payment remains visible even if the subsequent chat refresh fails.
    ++conversationReadRef.current;
    setConvo((current) => !current || current.thread.id !== threadId ? current : {
      ...current, messages: current.messages.map((message) => message.arrangementQuote?.id === quoteId
        ? { ...message, arrangementQuote: { ...message.arrangementQuote, status: "purchased", orderId } } : message),
    });
  }

  async function reviewArrangementPurchase(proposal: ArrangementQuote) {
    if (!user || !activeId || !proposal.request.mine || proposal.status !== "accepted" || quoteBusy || purchasePending.current) return;
    const request = conversationRequestRef.current;
    const threadId = activeId;
    purchasePending.current = true; setPurchaseBusy(true); setPurchaseError(null);
    try {
      const { quote } = await api.getItemArrangementQuote(proposal.id);
      if (request !== conversationRequestRef.current) return;
      if (quote.buyerUserId !== user.id || quote.itemId !== proposal.request.itemId || quote.threadId !== threadId) throw new Error("This proposal is not available for your purchase.");
      if (quote.status === "purchased" && quote.orderId) {
        markArrangementPurchased(quote.id, quote.orderId, threadId);
        return;
      }
      if (quote.status !== "accepted") throw new Error("This proposal is no longer accepted. Review the latest terms in Messages.");
      const total = quote.unitPrice * quote.qty + quote.fee;
      if (!Number.isSafeInteger(total) || total <= 0 || total > 2_000_000_000) throw new Error("The purchase total is outside the supported range.");
      // [items.checkout](../../../../docs/product/items.md#items-checkout): opening checkout reviews fresh agreed terms; only confirmation pays.
      const preview = await preparePurchaseReview({
        itemId: quote.itemId, title: proposal.request.itemTitle, seller: convo?.thread.otherName || "Seller",
        qty: quote.qty, unitPrice: quote.unitPrice, total, paymentMethod: "wallet", milestones: quote.milestones,
        handover: { method: quote.method, location: quote.location, schedule: quote.schedule, note: quote.note, fee: quote.fee, quoteId: quote.id },
      }, !!quote.purchaseOfferId);
      if (request === conversationRequestRef.current) setPurchaseTarget({ threadId, request, preview });
    } catch (failure: unknown) { if (request === conversationRequestRef.current) throw failure; }
    finally { purchasePending.current = false; if (request === conversationRequestRef.current) setPurchaseBusy(false); }
  }

  async function confirmArrangementPurchase() {
    if (!user || !purchaseTarget || purchasePending.current || purchaseTarget.threadId !== activeId || purchaseTarget.request !== conversationRequestRef.current) return;
    const { preview, threadId, request } = purchaseTarget;
    const quoteId = preview.handover?.quoteId;
    if (!quoteId) return;
    purchasePending.current = true; setPurchaseBusy(true); setPurchaseError(null);
    try {
      const { order } = await api.placeOrder(purchaseCheckoutInput(preview));
      if (request !== conversationRequestRef.current) return;
      if (!order) throw new Error("Could not complete the purchase. Try again.");
      markArrangementPurchased(quoteId, order.id, threadId);
      setPurchaseTarget(null);
      setAcceptanceToast("Purchase complete. View your purchase agreement below.");
      try { await refreshConversation(threadId, request); }
      catch { if (request === conversationRequestRef.current) setErr("Purchase complete, but the conversation could not refresh. Your purchase agreement is available below."); }
    } catch (failure: unknown) {
      if (request === conversationRequestRef.current) {
        if (preview.requestKey && (!(failure instanceof ApiError) || failure.status >= 500)) setPurchaseTarget(current => current ? { ...current, preview: { ...current.preview, uncertain: true } } : current);
        setPurchaseError(failure instanceof Error ? failure.message : "Could not complete the purchase. Try again.");
      }
    }
    finally { purchasePending.current = false; if (request === conversationRequestRef.current) setPurchaseBusy(false); }
  }

  return {
    quoteTarget, setQuoteTarget, arrangementTarget, setArrangementTarget, quoteBusy,
    setQuoteBusy, acceptanceToast, setAcceptanceToast, purchaseTarget, setPurchaseTarget,
    purchaseBusy, setPurchaseBusy, purchaseError, setPurchaseError, purchasePending,
    openServiceQuote, previewQuote, sendQuote, updateQuote, acceptArrangementRequest, updateItemQuote,
    sendArrangementQuote, decideArrangement, reviewArrangementPurchase, confirmArrangementPurchase,
  };
}
